import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { useResolvedById } from "@/shared/hooks/useResolvedById";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { sellerService } from "@/features/sellers/services/seller.service";
import type { SellerResponseDTO } from "@/features/sellers/types/seller.types";

interface SellerAsyncSelectProps {
  value: number | null;
  onChange: (sellerId: number | null) => void;
  disabled?: boolean;
  inputId?: string;
}

const DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 8;

export function SellerAsyncSelect({
  value,
  onChange,
  disabled = false,
  inputId,
}: SellerAsyncSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SellerResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchSellerById = useCallback(
    (id: number, signal: AbortSignal) => sellerService.getById(id, { signal }),
    [],
  );
  const [selected, preloadSeller, resolveError] = useResolvedById(
    value,
    fetchSellerById,
  );

  useEffect(() => {
    if (resolveError) {
      toast.error(
        getApiErrorMessage(resolveError, "Erro ao carregar vendedor"),
      );
    }
  }, [resolveError]);

  const fetchSellers = useCallback(
    async (term: string, signal: AbortSignal) => {
      const response = await sellerService.search(
        {
          name: term,
          size: SEARCH_PAGE_SIZE,
          sortBy: "name",
          sortDirection: "ASC",
        },
        { signal },
      );
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term: searchTerm,
    fetcher: fetchSellers,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar vendedores")),
    debounceMs: DEBOUNCE_MS,
  });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleSelect = useCallback(
    (seller: SellerResponseDTO) => {
      preloadSeller(seller.id, seller);
      onChange(seller.id);
      setSearchTerm("");
      setResults([]);
      setOpen(false);
    },
    [onChange, preloadSeller],
  );

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
        <span className="flex-1">{selected.name}</span>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          aria-label="Trocar vendedor"
          className="rounded-full p-0.5 hover:bg-accent"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          id={inputId}
          value={searchTerm}
          placeholder="Buscar vendedor pelo nome…"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          className="pl-9"
        />
      </div>

      {open && searchTerm.trim().length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto z-50">
          {searching ? (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4" />
              Buscando…
            </div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              Nenhum vendedor encontrado
            </div>
          ) : (
            results.map((seller) => (
              <button
                key={seller.id}
                type="button"
                onClick={() => handleSelect(seller)}
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
              >
                {seller.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
